import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AttributeTreeNode } from './state/useGenerationState';
import type { PromptModel } from './hooks/usePromptModels';
import { brandConfig, isTemplateOnlyHome } from '../../config/brands';
import styles from './GenerationWorkspace.module.css';
import '../../styles/form-controls.css';
import '../../styles/coach-mark.css';
import '../../styles/prompt-history.css';
import { GenerationPreviewPanel } from './preview/GenerationPreviewPanel';
import { MediaRequirementsAccordion } from './MediaRequirementsAccordion';
import { GENERATION_MODEL_SPEED, DEFAULT_GENERATION_MODEL, DEFAULT_SETTINGS, STORAGE_KEY_THINKING_LEVEL, readStoredThinkingLevel, type ThinkingLevel } from './state/useGenerationState';
import { useGenerationAssets } from './state/assetsContext';
import { getReferenceAudioMinClipSeconds, isBelowMinReferenceSize, isReferenceAudioBelowMinClipSeconds, isStillImageUsableAsset } from './state/assetMediaUtils';
import { SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR } from './scenario/constants';
import { isStoryboardIndividualRefsModel } from './scenario/utils';
import { useGenerationModels } from '../../features/generation-models/hooks';
import { useGenerationSettings } from './state/settingsContext';
import {
  TEMPLATE_SECTION_KEY,
  isCaptureRequiredOption,
  consumePendingTemplateSelection,
  findNodeByKey as findSelectedNode,
  captureTemplateTransientSettings,
  CAPTURE_TEMPLATE_TRANSIENT_KEYS,
} from './utils/templateCapture';
import { X } from 'lucide-react';
import { SelectedAssetsPanel } from './assets/SelectedAssetsPanel';
import { usePromptAttributes } from './hooks/usePromptAttributes';
import { usePromptModels } from './hooks/usePromptModels';
import { resolveModelParamQueryOutcome } from './utils/modelParamResolution';
import { normalizeEthnicitySegment } from '../../features/actors/actorAttributes';
import { useAllBrandUrls } from '../../features/brands/urls';
import { useGacha } from './hooks/useGacha';
import { PurposeModePanel } from './purpose/PurposeModePanel';
import { useUser } from '../../features/auth/useUser';
import { useMaterialsPanelStore } from '../../stores/materialsPanelStore';
import { ScenarioGenerator } from './scenario/ScenarioGenerator';
import { Tooltip } from '../common/Tooltip';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { AspectRatioPreview } from '../common/AspectRatioPreview';
import { useCoachMarkDismiss } from '../../hooks/useCoachMarkDismiss';
import { useAccordionState } from '../../hooks/useAccordionState';
import { useToast } from '../../hooks/useToast';
import { MOTION_LAYERED_LAYER_REQUIRED_NOTICE } from '../../features/motion/layeredPromptMode';
import { resolveVideoEditOfferAction } from './utils/videoEditOffer';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useQuery } from '@tanstack/react-query';
import { getCurrentUserProfile } from '../../features/profile/api';
// GenerationWorkspace の分割先 (計画書 3-2)。責務ごとのフックとセクションコンポーネント。
import { useStudioRouting } from './hooks/useStudioRouting';
import { useOrderedAttributeTree } from './hooks/useOrderedAttributeTree';
import { useAttributeRecommendations } from './hooks/useAttributeRecommendations';
import { useMediaSpecSuggestion } from './hooks/useMediaSpecSuggestion';
import { BannerLayoutEditorHost } from './bannerLayout/BannerLayoutEntry';
import { useBannerLayoutEditorEnabled } from '../../features/bannerLayout/useBannerLayoutEditorEnabled';
import { isBannerLayoutNode } from '../../features/bannerLayout/attributeNode';
import { useSelectedAssetBrands } from './hooks/useSelectedAssetBrands';
import { ToggleSwitch } from '../common/ToggleSwitch';
import { useVideoModelCapabilities } from './hooks/useVideoModelCapabilities';
import { CompositionSection } from './workspace/CompositionSection';
import { PersonSection } from './workspace/PersonSection';
import { TextSection } from './workspace/TextSection';
import { loadGenerateTextPreference } from './state/settingsStorage';
import { ExtraGroupSection } from './workspace/ExtraGroupSection';
import { TemplateSection } from './workspace/TemplateSection';
import { useMotionGraphicsTabVisible } from '../../features/motion/useMotionGraphicsTabVisible';
import { useMotionLayerModalStore } from '../../features/motion/layerModalStore';

export default function GenerationWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedAssets,
    clearAssets,
    activeScenarioId,
    setActiveScenarioId,
    reorderAssets,
    removeAsset,
    videoEditOffer,
    clearVideoEditOffer,
    beginVideoEdit,
    setPromptMotionModeActive,
  } = useGenerationAssets();
  const {
    settings,
    setSettings,
    applyTransientSettings,
    setAttribute,
    attributeTree,
    setAttributeTree,
    templateReferenceUrl,
    setTemplateReferenceUrl,
    pendingPrompt,
    pendingMode,
    setPendingEdit,
    setPendingVideoGeneration,
    clearPendingEdit,
    selectedCasting,
    setSelectedCasting,
    additionalCastings,
    setAdditionalCastings,
    additionalSeedancePresetActors,
    setAdditionalSeedancePresetActors,
    selectedSeedancePresetActor,
    setSelectedSeedancePresetActor,
    videoGenerationMode,
    promptVideoGenerationMode,
    videoSettings,
    setVideoSettings,
    setMaxReferenceVideos,
    setMaxReferenceAudios,
    castingPosition,
    setCastingPosition,
    visualStyleIsStylized,
    productionMode,
    restoreSettings,
  } = useGenerationSettings();

  const openMaterialsPanel = useMaterialsPanelStore((state) => state.open);
  const closeMaterialsPanel = useMaterialsPanelStore((state) => state.close);
  const toast = useToast();
  const isMobile = useIsMobile();
  const { randomize, randomizeWithRecommendations } = useGacha();

  // 商品AIプロファイル由来の推奨 (計画書§5.2 / §5.3 / §8 P4) → hooks/useAttributeRecommendations
  const {
    recommendationSubject,
    recommendationsQuery,
    gachaMode,
    gachaTooltip,
    gachaLabel,
    recommendedKeysByGroup,
    handleAttributeChange,
    logRecommendationEvent,
  } = useAttributeRecommendations({ selectedAssets, setAttribute });

  // モバイルで素材が選択されたらヘッダーを隠す
  useEffect(() => {
    if (isMobile && selectedAssets.length > 0) {
      document.body.classList.add('mobile-assets-selected');
      return () => {
        document.body.classList.remove('mobile-assets-selected');
      };
    }
  }, [isMobile, selectedAssets.length]);

  // セーフモード取得（ガチャ用）
  const safeModeQuery = useQuery({
    queryKey: ['userProfile', 'safeMode'],
    queryFn: async () => {
      const profile = await getCurrentUserProfile();
      // ロック中(partner 強制等)は effective_safe_mode が true。raw safe_mode より優先。
      return profile.effective_safe_mode ?? profile.safe_mode ?? true;
    },
    staleTime: 5 * 60 * 1000,
  });

  const attributeQuery = usePromptAttributes();

  useEffect(() => {
    if (attributeQuery.data?.items?.length) {
      setAttributeTree(attributeQuery.data.items as AttributeTreeNode[]);
    }
  }, [attributeQuery.data, setAttributeTree]);

  // ガチャ実行中はoverall_impression→aspect_ratio提案をスキップ
  const skipAspectRatioSyncRef = useRef(false);
  // ガチャ実行中は媒体規格 (media_spec_id) 提案もスキップ
  const skipMediaSpecSuggestionRef = useRef(false);

  // metadata.media_spec_id 付き option の選択を検知して媒体規格の適用を提案
  const {
    suggestion: mediaSpecSuggestion,
    accept: acceptMediaSpecSuggestion,
    dismiss: dismissMediaSpecSuggestion,
  } = useMediaSpecSuggestion({
    attributeTree,
    attributes: settings.attributes as Record<string, unknown> | undefined,
    setSettings,
    suppressOnceRef: skipMediaSpecSuggestionRef,
  });

  // アスペクト比変更の確認ダイアログ
  const [aspectRatioConfirm, setAspectRatioConfirm] = useState<{
    targetRatio: string;
    currentRatio: string;
    reason: string;
  } | null>(null);

  // overall_impression変更時にアスペクト比の変更を提案
  // 注意: overall_impressionの変更時のみ発火させる。
  // attributeTreeやaspect_ratioを依存配列に含めると意図しない再発火が起きる。
  const overallImpression = settings.attributes?.overall_impression as string | undefined;
  useEffect(() => {
    if (skipAspectRatioSyncRef.current) {
      skipAspectRatioSyncRef.current = false;
      return;
    }

    if (!overallImpression || overallImpression === 'none') {
      return;
    }

    // overall_impression は仮想グループ(composition/style)のどこに移動しても拾えるよう再帰探索
    const overallImpressionNode = findSelectedNode(attributeTree, 'overall_impression');
    if (!overallImpressionNode) return;

    const selectedOption = overallImpressionNode.children?.find((child) => child.key === overallImpression);
    if (!selectedOption?.metadata?.aspect_ratio) return;
    // 媒体規格の紐付けがある場合はそちらの提案 (useMediaSpecSuggestion) に譲る
    if (selectedOption.metadata.media_spec_id) return;

    const targetAspectRatio = selectedOption.metadata.aspect_ratio as string;
    // 既に同じアスペクト比なら何もしない
    if (settings.aspect_ratio === targetAspectRatio) return;

    // 確認ダイアログを表示
    const optionLabel = selectedOption.label || overallImpression;
    setAspectRatioConfirm({
      targetRatio: targetAspectRatio,
      currentRatio: settings.aspect_ratio || 'none',
      reason: `${optionLabel}は ${targetAspectRatio} のアスペクト比（縦横比）がオススメです。変更しますか？`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overallImpression]);

  // パートナー既定値 (organization_attribute_overrides.default_option_key)。
  // 対象は背景・構図 (composition) 直下のフィールド group のみ。
  const partnerDefaultAttributes = useMemo(() => {
    const compositionNode = attributeTree?.find((n) => n.key === 'composition');
    const map: Record<string, string> = {};
    for (const child of compositionNode?.children ?? []) {
      if (child.default_option_key) map[child.key] = child.default_option_key;
    }
    return map;
  }, [attributeTree]);

  // 既定値の注入 (初回ロード時に1回だけ)。attributes は localStorage 永続化されず
  // 毎回 DEFAULT_SETTINGS から始まり、主要キーは 'none' がシードされているため、
  // undefined だけでなく 'none' (シード値) も注入対象にする。ref ガードで一度きりに
  // するので、セッション中にユーザーが明示的に「指定なし」へ戻した選択は再注入しない。
  // 「すべてリセット」時は handleResetAll 側で既定値込みの attributes を組み立てる。
  // 注入後は通常の選択と同一扱いなので、枠線強調・サマリー・プロンプト反映も既存経路に乗る。
  const partnerDefaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (partnerDefaultsAppliedRef.current) return;
    const keys = Object.keys(partnerDefaultAttributes);
    if (keys.length === 0) return; // ツリー未着 or 既定値なし
    partnerDefaultsAppliedRef.current = true;
    for (const key of keys) {
      const current = settings.attributes?.[key];
      if (current === undefined || current === 'none') {
        setAttribute(key, partnerDefaultAttributes[key]);
      }
    }
  }, [partnerDefaultAttributes, settings.attributes, setAttribute]);

  // 選択中の背景がロゴを必要とするか判定
  const selectedBackgroundRequiresLogo = useMemo(() => {
    const bg = settings.attributes?.background as string | undefined;
    if (!bg || bg === 'none') return false;
    // composition root 配下に移動したので平坦化して探す
    const bgNode = attributeTree
      ?.flatMap((n) => (n.key === 'composition' ? (n.children ?? []) : [n]))
      .find((n) => n.key === 'background');
    const selected = findSelectedNode(bgNode?.children, bg);
    return !!selected?.metadata?.requires_logo;
  }, [settings.attributes?.background, attributeTree]);

  // requires_logo な背景選択時に include_logo を自動ON/OFF
  const prevRequiresLogoRef = useRef(selectedBackgroundRequiresLogo);
  useEffect(() => {
    if (selectedBackgroundRequiresLogo && !settings.include_logo) {
      setSettings((prev) => ({ ...prev, include_logo: true }));
    } else if (!selectedBackgroundRequiresLogo && prevRequiresLogoRef.current && settings.include_logo) {
      setSettings((prev) => ({ ...prev, include_logo: false }));
    }
    prevRequiresLogoRef.current = selectedBackgroundRequiresLogo;
  }, [selectedBackgroundRequiresLogo, settings.include_logo, setSettings]);

  // excludePeople = 「人物・キャラクターを含めない」(attributes.person.none)。ON のときは選択中の
  // 俳優を解除する。俳優カルーセル自体は隠さず、絞り込み (性別 / 人種 / 年代) もそのまま維持する
  // (隠す・絞り込みを外すとパネルの高さや一覧の並びが動くため。PersonSection がグレーアウトして見せる)
  const { filters, excludePeople } = useMemo(() => {
    const attrs = (settings.attributes as Record<string, unknown>) || {};
    const person = (attrs.person as Record<string, unknown> | undefined) || {};
    const nonePerson = person.none === true;

    const filters: { gender?: string; ethnicity?: string; age?: number } = {};

    if (typeof person.gender === 'string' && person.gender !== 'none') {
      filters.gender = person.gender as string;
    }
    if (typeof person.ethnicity === 'string' && person.ethnicity !== 'none') {
      filters.ethnicity = person.ethnicity as string;
    }
    if (typeof person.age === 'number') {
      filters.age = person.age as number;
    }

    return {
      filters,
      excludePeople: nonePerson,
    };
  }, [settings]);

  const normalizedFilters = useMemo(() => {
    const result: Record<string, string | number> = {};
    if (filters.gender) {
      result.gender = filters.gender;
    }
    if (filters.ethnicity) {
      result.ethnicity = filters.ethnicity;
    }
    if (typeof filters.age === 'number' && !Number.isNaN(filters.age)) {
      result.age = filters.age;
    }
    return result;
  }, [filters.gender, filters.ethnicity, filters.age]);

  const modelsQuery = usePromptModels(normalizedFilters);
  const [selectedModelId, setSelectedModelId] = useState<string | undefined>(settings.model);

  // 生成履歴からの復元等で settings.model が外部変更された際に、modelMeta / selectedModelId を同期。
  // modelsQuery.data が未ロードなら後のレンダリングで再評価される。
  useEffect(() => {
    const modelId = settings.model;
    const metaId = (settings.modelMeta as { id?: string } | null)?.id;
    if (modelId && modelId !== metaId) {
      setSelectedModelId(modelId);
      const found = modelsQuery.data?.find((m) => m.id === modelId);
      if (found) {
        setSettings((prev) => ({ ...prev, modelMeta: found }));
      }
    } else if (!modelId && metaId) {
      setSelectedModelId(undefined);
      setSettings((prev) => ({ ...prev, modelMeta: null }));
    }
  }, [settings.model, settings.modelMeta, modelsQuery.data, setSettings]);

  // 選択素材からブランド (ロゴ / タグライン / 説明) を解決 → hooks/useSelectedAssetBrands
  const {
    extractedAsins,
    hasAnyProductLinks,
    brandDescriptionAvailable,
    brandLogosAvailable,
    brandTaglines,
    brandsWithoutLogos,
    brandLogosQuery,
  } = useSelectedAssetBrands(selectedAssets);
  // 選択素材からブランドが1つでも解決できるか (プロンプト入力モードの「ブランド情報」トグルの表示条件)
  const brandsAvailable = (brandLogosQuery.data ?? []).length > 0;

  // 「案件情報からのテキスト生成」の既定: 未選択 (undefined) のときだけ、素材の紐づきに合わせて当てる。
  // - 保存済みの選択が無い → 案件に結びつく素材があれば「生成する」、無ければ指定なしのまま
  // - 保存済みが「生成する」→ 案件/ブランド情報があれば「生成する」
  // - 情報が無くなったら「生成する」を外して指定なしに戻す (保存済みの選択は残すので、紐づけ直すと戻る)
  // 明示の「指定なし」(false)・「生成しない」('never') は触らない。自動で当てた値は localStorage に保存しない
  const generateTextFromEcValue = settings.attributes?.generate_text_from_ec;
  const brandInfoLoading = brandLogosQuery.isLoading;
  useEffect(() => {
    const canGenerateFromInfo = hasAnyProductLinks || brandDescriptionAvailable;
    if (generateTextFromEcValue === undefined) {
      const pref = loadGenerateTextPreference();
      const shouldGenerate = pref === 'generate' ? canGenerateFromInfo : pref === null && hasAnyProductLinks;
      if (shouldGenerate) setAttribute('generate_text_from_ec', true);
    } else if (generateTextFromEcValue === true && !canGenerateFromInfo && !brandInfoLoading) {
      setAttribute('generate_text_from_ec', undefined);
    }
  }, [generateTextFromEcValue, hasAnyProductLinks, brandDescriptionAvailable, brandInfoLoading, setAttribute]);

  // バナーレイアウトエディタ (docs/features/banner-layout-template-plan.md)。段階公開のフラグで出し分け、
  // さらに属性ツリーに「レイアウト」ノード (composition 直下の固定フィールド・§19) が見えていること。
  // パートナーが org-admin で非表示にすると exclusion でツリーから消え、入口・エディタ・送信値とも止まる
  const isBannerLayoutFlagEnabled = useBannerLayoutEditorEnabled();
  const hasBannerLayoutNode = useMemo(
    () => !!attributeTree?.find((n) => n.key === 'composition')?.children?.some(isBannerLayoutNode),
    [attributeTree],
  );
  const isBannerLayoutEnabled = isBannerLayoutFlagEnabled && hasBannerLayoutNode;
  const bannerLayout = isBannerLayoutEnabled ? settings.banner_layout ?? null : null;
  const logoBrandNames = useMemo(
    () => (brandLogosQuery.data ?? []).filter((b) => b.logos && b.logos.length > 0).map((b) => b.brand_name),
    [brandLogosQuery.data],
  );

  useEffect(() => {
    setSelectedModelId(settings.model);
  }, [settings.model]);

  useEffect(() => {
    if (excludePeople && (selectedModelId || settings.model)) {
      setSelectedModelId(undefined);
      setSettings((prev) => {
        if (!prev.model && !prev.modelMeta) {
          return prev;
        }
        return {
          ...prev,
          model: undefined,
          modelMeta: null,
        };
      });
    }
  }, [excludePeople, selectedModelId, settings.model, setSettings]);

  useEffect(() => {
    if (!selectedModelId) return;
    setSettings((prev) => {
      const prevPerson = (prev.attributes?.person as Record<string, unknown>) ?? {};
      return {
        ...prev,
        attributes: {
          ...prev.attributes,
          person: {
            ...prevPerson,
            none: false,
          },
        },
      };
    });
  }, [selectedModelId, setSettings]);

  // 属性ツリーの整形 (仮想 root の平坦化 / セクション順 / OEM 追加 group / テンプレート group)
  // → hooks/useOrderedAttributeTree
  const {
    orderedAttributeTree,
    accordionOrderFor,
    hasComposition,
    hasPerson,
    extraTopLevelGroups,
    templateGroups,
    visibleTemplateGroups,
  } = useOrderedAttributeTree(attributeTree, attributeQuery.data?.sections);

  // テンプレート特化ホーム (usodeki バリアント): テンプレートアコーディオンと
  // 素材/生成まわりだけを出し、他のセクション (背景・構図/人物/OEM group/テキスト) と
  // タブ・モード切替は表示しない (静止画・属性モード固定)。
  const isTemplateOnlyUi = isTemplateOnlyHome;

  // 目的軸モード (計画書§6) は当面 platform 組織 (LightMedia) のメンバー限定で公開する
  // (2026-07-12 ユーザー指示)。JWT の is_platform で判定 (useUser.isPlatformOrg)。
  // 全体公開する際はこのゲートを外すだけでよい。
  const { isPlatformOrg, user: authUser } = useUser();
  const isPurposeModeAvailable = isPlatformOrg;

  // 選択中テンプレート: attributes[group.key] が 'none' 以外の文字列の最初の group
  const activeTemplate = useMemo(() => {
    for (const group of templateGroups) {
      const value = settings.attributes?.[group.key];
      if (typeof value === 'string' && value && value !== 'none') {
        const optionNode = findSelectedNode(group.children, value);
        return {
          group,
          optionKey: value,
          label: optionNode?.label ?? value,
          // metadata.capture_required (DB 駆動) + optionKey フォールバック: URL/参照画像必須テンプレか
          captureRequired: isCaptureRequiredOption(optionNode, value),
        };
      }
    }
    return null;
  }, [templateGroups, settings.attributes]);
  const isTemplateActive = activeTemplate !== null;
  // activeTemplate が purpose_only (バナー等・目的軸モード専用) の group か。
  // PurposeModePanel の初期タブ決定 (計画書の目的タブ状態保持) に使う。
  const activeTemplateIsPurposeOnly =
    !!activeTemplate && (activeTemplate.group.metadata as { purpose_only?: boolean } | null | undefined)?.purpose_only === true;
  const templateLockClass = isTemplateActive ? ` ${styles.templateLocked}` : '';
  // テンプレート適用中はドロップダウン自体がロックされるため推奨表示も出さない
  const activeRecommendedKeysByGroup = isTemplateActive ? undefined : recommendedKeysByGroup;

  // ブランドURL入力補助: キャプチャ必須テンプレート選択時のみフェッチ。
  // URL を持つブランドが 0 件なら何も表示せず、既存のURL入力欄だけになる。
  // ブランド/サイト選択の状態は TemplateUrlPicker 内で管理する。
  const { data: brandUrlChoices } = useAllBrandUrls(activeTemplate?.captureRequired ?? false);

  const selectTemplateOption = (groupKey: string, optionKey: string) => {
    // 他の template group の選択をクリアして単一選択を保証する
    for (const other of templateGroups) {
      if (other.key !== groupKey) {
        setAttribute(other.key, 'none');
      }
    }
    setAttribute(groupKey, optionKey);
    // キャプチャ必須テンプレート (metadata.capture_required) は GPT Image 2 +
    // プラットフォーム指定なしを初期値として流し込む。ユーザーは後から変更可能。
    // transient にして localStorage のモデル選好・プラットフォーム記憶は汚さない。
    const group = templateGroups.find((g) => g.key === groupKey);
    const optionNode = group ? findSelectedNode(group.children, optionKey) : undefined;
    if (isCaptureRequiredOption(optionNode, optionKey)) {
      applyTransientSettings(captureTemplateTransientSettings(), CAPTURE_TEMPLATE_TRANSIENT_KEYS);
    }
  };

  const handleModelToggle = (model: PromptModel) => {
    const next = selectedModelId === model.id ? undefined : model.id;
    setSelectedModelId(next);
    setSettings((prev) => ({
      ...prev,
      model: next,
      modelMeta: next ? model : null,
    }));
  };

  // 旧URLパラメータ（互換性のため）は useStudioRouting 側で読む。
  // ここで読むのは素材モーダル / 俳優 / コーチマーク用の単発パラメータのみ。
  const modelParam = searchParams.get('model');
  const guideParam = searchParams.get('guide');
  const materialIdParam = searchParams.get('material_id');

  // URLパラメータから素材詳細モーダルを自動表示
  const materialIdParamProcessed = useRef(false);
  useEffect(() => {
    if (materialIdParamProcessed.current || !materialIdParam) return;
    materialIdParamProcessed.current = true;
    useMaterialsPanelStore.getState().openMaterialDetail(materialIdParam);
  }, [materialIdParam]);

  // URLパラメータからモデルを選択（初回のみ）。ref のブール値ではなく処理済み ID を
  // 保持し、modelParam が変わったら再処理できるようにする
  // (docs/features/character-casting-consistency-plan.md 修正1-b)。
  const modelParamProcessedIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    // 既に処理済み、またはmodelParamがない場合はスキップ
    if (!modelParam || modelParamProcessedIds.current.has(modelParam)) return;

    // person.none を false にしてモデルセクションを表示
    setSettings((prev) => {
      const prevPerson = (prev.attributes?.person as Record<string, unknown>) ?? {};
      if (prevPerson.none === false) return prev;
      return {
        ...prev,
        attributes: {
          ...prev.attributes,
          person: {
            ...prevPerson,
            none: false,
          },
        },
      };
    });

    // 「人物」アコーディオンを開く
    setIsPersonSectionOpen(true);
    // setIsPersonSectionOpen は useAccordionState (useState) 由来で参照が安定しており、
    // 宣言はこの effect より下にあるため deps には入れない (TDZ になる)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelParam, setSettings]);

  // ?model= の単体解決。一覧表示用の modelsQuery は現在の人物属性 (person.ethnicity 等)
  // でフィルタされているため、既定の person.ethnicity='jp' のままではキャラクター俳優が
  // ヒットしない。ids 指定の専用クエリで単体解決する。
  const modelParamQuery = usePromptModels(
    modelParam ? { ids: [modelParam] } : {},
    { enabled: Boolean(modelParam) },
  );
  useEffect(() => {
    if (!modelParam || modelParamProcessedIds.current.has(modelParam)) return;

    const outcome = resolveModelParamQueryOutcome(modelParam, {
      data: modelParamQuery.data,
      isError: modelParamQuery.isError,
    });
    if (outcome.kind === 'loading') return;
    if (outcome.kind === 'error') {
      modelParamProcessedIds.current.add(modelParam);
      toast.error('AI俳優情報の取得に失敗しました');
      return;
    }
    if (outcome.kind === 'not_found') {
      modelParamProcessedIds.current.add(modelParam);
      toast.error('指定された俳優が見つかりませんでした');
      return;
    }

    const targetModel = outcome.target;
    modelParamProcessedIds.current.add(modelParam);
    setSelectedModelId(targetModel.id);
    setSettings((prev) => {
      const currentPerson = (prev.attributes?.person as Record<string, unknown>) ?? {};
      return {
        ...prev,
        model: targetModel.id,
        modelMeta: targetModel,
        attributes: {
          ...prev.attributes,
          person: {
            ...currentPerson,
            ethnicity: normalizeEthnicitySegment(targetModel.ethnicity),
          },
        },
      };
    });

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('model');
      return next;
    }, { replace: true });
  }, [modelParam, modelParamQuery.data, modelParamQuery.isError, setSettings, setSearchParams, toast]);

  // URL ↔ タブ / モードの双方向同期 (旧URL互換・無限ピンポン回避を含む)
  // → hooks/useStudioRouting
  const motionTabVisible = useMotionGraphicsTabVisible();
  // レイヤー素材のモーダルを開く要求 (モーダル本体は PromptModePanel が motion 中だけ描く)
  const openLayerModal = useMotionLayerModalStore((s) => s.openLayerModal);
  const {
    location,
    navigate,
    activeTab,
    stillImageMode,
    scenarioMode,
    handleTabChange,
    handleModeChange,
    handleScenarioModeChange,
  } = useStudioRouting({
    activeScenarioId,
    setActiveScenarioId,
    openMaterialsPanel,
    closeMaterialsPanel,
    isMobile,
    isPurposeModeAvailable,
    authUser,
    pendingMode,
  });

  // 動画モデルの能力 (参考画像枚数上限 / video-to-video) と切替確認ダイアログ
  // → hooks/useVideoModelCapabilities
  const {
    referenceImageLimit,
    v2vSwitchConfirm,
    confirmV2vSwitch,
    cancelV2vSwitch,
  } = useVideoModelCapabilities({
    videoSettings,
    setVideoSettings,
    selectedAssets,
    setMaxReferenceVideos,
    setMaxReferenceAudios,
    removeAsset,
  });

  // 動画素材が選べないコンテキストで動画が選ばれたときの扱い (コンテキスト別)。
  //  - 動画プロンプト入力モード: v2v 非対応モデルを選んでいるケース。従来どおり即座に
  //    切り替える (beginVideoEdit が対応モデルへ自動切替する。画面遷移は実質同一画面)。
  //  - 静止画タブ: 確認モーダルを出し、ユーザーが選んだときだけ切り替える。
  //  - シナリオタブ: 切り替えない。シーン単位生成は参照動画を扱わないため、トーストで
  //    断って明示導線 (素材フッターの「AIに修正依頼」) に誘導する。
  //    (旧実装は無条件で /studio/video/prompt へ遷移していたため、シナリオの編集中に
  //     動画素材をクリックしただけで画面が飛んでいた)
  const showVideoEditConfirm = !!videoEditOffer && activeTab === 'stillImage';
  const acceptVideoEditOffer = useCallback(() => {
    if (!videoEditOffer) return;
    beginVideoEdit(videoEditOffer);
    clearVideoEditOffer();
    navigate('/studio/video/prompt');
    toast.show('動画プロンプト入力モードに切り替えました');
  }, [videoEditOffer, beginVideoEdit, clearVideoEditOffer, navigate, toast]);
  useEffect(() => {
    if (!videoEditOffer) return;
    // 判定は resolveVideoEditOfferAction (utils/videoEditOffer.ts) に一本化
    const action = resolveVideoEditOfferAction({ activeTab, productionMode, promptVideoGenerationMode });
    if (action === 'confirm') return;  // 静止画タブ: 確認モーダルの応答待ち
    if (action === 'rejectScenario') {
      clearVideoEditOffer();
      toast.show('動画素材はシナリオの動画生成には使えません。動画を編集するときは素材の「AIに修正依頼」から');
      return;
    }
    if (action === 'rejectMotion') {
      // プロンプト入力モードの「モーショングラフィックス」は動画編集へ切り替えない (motion から外れる)。
      // レイヤー素材だけを受ける旨を案内して止める (docs/features/motion-graphics-mode-plan.md §3.4・§5.2)
      clearVideoEditOffer();
      toast.show(MOTION_LAYERED_LAYER_REQUIRED_NOTICE);
      return;
    }
    acceptVideoEditOffer();
  }, [videoEditOffer, activeTab, productionMode, promptVideoGenerationMode, acceptVideoEditOffer, clearVideoEditOffer, toast]);

  // 属性選択モード コーチマーク
  const [coachMarkMessage, setCoachMarkMessage] = useState<string | null>(null);
  const attributeModeButtonRef = useRef<HTMLButtonElement>(null);
  const dismissCoachMark = useCallback(() => setCoachMarkMessage(null), []);
  useCoachMarkDismiss(attributeModeButtonRef, !!coachMarkMessage, dismissCoachMark);
  useEffect(() => {
    if (guideParam === 'safe-mode') {
      setCoachMarkMessage(`セーフモードを解除しました。${brandConfig.labels.attributeMode}で生成すると、お望みの画像が生成される可能性が高まります。`);
      navigate(location.pathname, { replace: true });
    } else if (guideParam === 'attributes') {
      setCoachMarkMessage(`${brandConfig.labels.attributeMode}で生成すると、お望みの画像が生成される可能性が高まります。`);
      navigate(location.pathname, { replace: true });
    }
  }, [guideParam, navigate, location.pathname]);

  // 属性モード「その他のプロンプト」履歴 (全画面モーダル。docs/features/prompt-history-db-plan.md)
  const [showAttrPromptHistory, setShowAttrPromptHistory] = useState(false);

  const handleSelectAttrPromptHistory = useCallback((prompt: string) => {
    setAttribute('other_instructions', prompt);
  }, [setAttribute]);

  const [isSticky, setIsSticky] = useState(false);
  const [isStickyCollapsed, setIsStickyCollapsed] = useState(false);
  const [isAssetsScrolledToEnd, setIsAssetsScrolledToEnd] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);
  const stickySentinelRef = useRef<HTMLDivElement>(null);

  // アコーディオンの開閉状態は localStorage に永続化する (hooks/useAccordionState)。
  // プロンプト入力モードの間は「全部閉じた」状態を保存しない (persist=false) ことで、
  // 属性モードへ戻ったときに元の開閉状態が復元される。
  const accordionPersist = { persist: stillImageMode !== 'prompt' };
  const [isImageSectionOpen, setIsImageSectionOpen] = useAccordionState('accordion_image_section_open', false, accordionPersist);
  const [isPersonSectionOpen, setIsPersonSectionOpen] = useAccordionState('accordion_person_section_open', false, accordionPersist);
  // テンプレート適用中は背景・構図/人物アコーディオンを閉じて操作不可にする
  // (実 state は保持し、テンプレート解除で元に戻す)。
  const compositionRenderOpen = isImageSectionOpen && !isTemplateActive;
  const personRenderOpen = isPersonSectionOpen && !isTemplateActive;
  // スタイルセクションの UI は背景・構図へ統合済み。開閉状態のみ後方互換で維持する。
  const [, setIsStyleSectionOpen] = useAccordionState('accordion_style_section_open', false, accordionPersist);
  const [thinkingLevel, setThinkingLevel] = useState<ThinkingLevel>(readStoredThinkingLevel);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_THINKING_LEVEL, thinkingLevel);
  }, [thinkingLevel]);
  const [isTextSectionOpen, setIsTextSectionOpen] = useAccordionState('accordion_text_section_open', false, accordionPersist);
  const [isMediaRequirementsOpen, setIsMediaRequirementsOpen] = useAccordionState('accordion_media_requirements_open', false, accordionPersist);
  // OEM が追加したトップレベル group 用の汎用アコーディオン開閉状態
  const [extraAccordionOpen, setExtraAccordionOpen] = useState<Record<string, boolean>>({});
  const toggleExtraAccordion = (key: string) =>
    setExtraAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  // ホーム (テンプレート一覧) から遷移した場合、指定されたテンプレートを初期適用する。
  // ツリー到着後の一度だけ localStorage を消費し、ノードが実在する場合のみ適用する。
  const pendingTemplateProcessed = useRef(false);
  useEffect(() => {
    if (pendingTemplateProcessed.current || templateGroups.length === 0) return;
    pendingTemplateProcessed.current = true;
    const pending = consumePendingTemplateSelection();
    if (!pending) return;
    const group = templateGroups.find((g) => g.key === pending.groupKey);
    if (!group || !findSelectedNode(group.children, pending.optionKey)) return;
    selectTemplateOption(pending.groupKey, pending.optionKey);
    setExtraAccordionOpen((prev) => ({ ...prev, [pending.groupKey]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateGroups]);

  // テンプレート特化 UI ではテンプレートアコーディオンを既定で展開する。
  // purpose_only (目的軸モード専用) group はここでも対象外。
  useEffect(() => {
    if (!isTemplateOnlyUi || visibleTemplateGroups.length === 0) return;
    setExtraAccordionOpen((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const group of visibleTemplateGroups) {
        if (!(group.key in next)) {
          next[group.key] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isTemplateOnlyUi, visibleTemplateGroups]);

  // プロンプト入力モードの場合、すべてのアコーディオンを閉じる（LocalStorageを無視）
  useEffect(() => {
    if (stillImageMode === 'prompt') {
      setIsImageSectionOpen(false);
      setIsPersonSectionOpen(false);
      setIsStyleSectionOpen(false);
      setIsTextSectionOpen(false);
      setIsMediaRequirementsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stillImageMode]);

  // すべてのアコーディオンを開く/閉じる
  const openAllAccordions = () => {
    setIsImageSectionOpen(true);
    setIsPersonSectionOpen(true);
    setIsStyleSectionOpen(true);
    setIsTextSectionOpen(true);
    setIsMediaRequirementsOpen(true);
  };

  const closeAllAccordions = () => {
    setIsImageSectionOpen(false);
    setIsPersonSectionOpen(false);
    setIsStyleSectionOpen(false);
    setIsTextSectionOpen(false);
    setIsMediaRequirementsOpen(false);
  };

  // 1ボタントグルの表示判定: 表示中の全セクションが開いていれば「すべて閉じる」。
  // 非表示のセクション (属性ツリーに構図/人物が無い場合) は判定から除く。
  // スタイルセクションは描画されておらず開閉値も参照されないため含めない。
  const areAllAccordionsOpen =
    (!hasComposition || isImageSectionOpen)
    && (!hasPerson || isPersonSectionOpen)
    && isTextSectionOpen
    && isMediaRequirementsOpen;

  // すべてリセット（コンテンツ系の選択をデフォルト値に戻す。思考レベルはリセットしない）
  // 旧実装は `...prev` を維持していたため、属性ツリー以外のトップレベル設定
  // (include_logo / exclude_text_response / aspect_ratio / compliance_overrides /
  //  レガシー background・hand・gender 等) がリセットされない不具合があった。
  // 2026-09-09 のユーザー指示で媒体要件の中身 (生成モデル・解像度・出力形式・背景指定・
  // 自動リサイズ・媒体選択) もすべて既定に戻す。prev から引き継ぐものは無い。
  //   - 生成モデル: DEFAULT_GENERATION_MODEL (GPT Image 2.5 標準・自動)
  //   - 解像度: 1K (GenerationPreviewPanel の初期化と同じ既定。GPT 系の保存キー image_size_quality
  //     も 1K に戻し、リロード後に旧値が復活しないようにする)
  //   - 出力形式 / 背景 / 目標サイズ: 未指定、自動リサイズ: 既定 (ON)
  //   - 媒体 (プラットフォーム / メニュー / 仕様 / 規定): DEFAULT_SETTINGS の null / []
  //   generation_model / aspect_ratio / media_platform_id の localStorage 反映は
  //   settingsContext の永続化エフェクトが行う。
  // attributes は setSettings だと prev と浅くマージされるため、DEFAULT_SETTINGS に
  // シードされていないキー (季節・視点・被写体サイズ・構図・F値など、ランダム選択が
  // 書き込むもの) が残ってしまう (2026-09-09 の不具合)。丸ごと置換する restoreSettings を使う。
  const handleResetAll = useCallback(() => {
    restoreSettings({
      ...DEFAULT_SETTINGS,
      // パートナー既定値 (default_option_key) はリセット後も適用された状態に戻す
      attributes: { ...DEFAULT_SETTINGS.attributes, ...partnerDefaultAttributes },
      model: undefined,
      modelMeta: null,
      generation_model: DEFAULT_GENERATION_MODEL,
      image_size: '1K',
      output_format: undefined,
      image_background: undefined,
      target_width: undefined,
      target_height: undefined,
      auto_trim_resolution: undefined,
    });
    localStorage.setItem('image_size_quality', '1K');
    setSelectedModelId(undefined);
  }, [restoreSettings, partnerDefaultAttributes]);

  // AIおまかせ（ガチャ置換。計画書§5.3）: 商品AIプロファイルの推奨が ready なら
  // それに沿って一発組みする。pending/failed/unsupported/素材未選択時は
  // 従来の完全ランダム抽選にフォールバックする（gachaMode==='pending' はボタン自体を
  // 無効化しているため、ここに到達するのは ready か random のいずれか）。
  const handleGacha = useCallback(() => {
    // アスペクト比を保持（overall_impressionの自動連動で上書きされるのを防ぐ）
    const currentAspectRatio = settings.aspect_ratio;
    skipAspectRatioSyncRef.current = true;
    skipMediaSpecSuggestionRef.current = true;

    // テンプレート選択中はガチャ結果が反映されないため、先に解除する
    for (const group of templateGroups) {
      setAttribute(group.key, 'none');
    }

    // 選択実行: プロファイル ready ならAIおまかせ、それ以外は完全ランダム
    const isSafeMode = safeModeQuery.data ?? true;
    const recommendations = recommendationsQuery.data;
    const isRecommendationReady = recommendations?.status === 'ready';
    const result = isRecommendationReady
      ? randomizeWithRecommendations({
        attributeTree: orderedAttributeTree,
        currentAttributes: settings.attributes,
        isSafeMode,
        recommendations,
      })
      : randomize({
        attributeTree: orderedAttributeTree,
        currentAttributes: settings.attributes,
        isSafeMode,
      });

    // AIおまかせボタンのクリックログ (計画書§8 P4)。ready なら商品プロファイルに
    // 沿った採用、それ以外 (pending/failed/unsupported/素材未選択) は完全ランダムへの
    // フォールバックとして記録する。
    logRecommendationEvent({
      subject_type: recommendationSubject?.subject_type ?? null,
      subject_id: recommendationSubject?.subject_id ?? null,
      surface: 'ai_omakase',
      action: isRecommendationReady ? 'adopted' : 'random_fallback',
      profile_status: recommendations?.status ?? 'none',
    });

    // 結果を属性に適用
    for (const [key, value] of Object.entries(result)) {
      setAttribute(key, value);
    }

    // person グループの設定: ethnicity=jp固定、none=false
    const currentPerson = (settings.attributes?.person as Record<string, unknown>) || {};
    const resultPerson = (result.person as Record<string, unknown>) || {};
    setAttribute('person', {
      ...currentPerson,
      ...resultPerson,
      ethnicity: 'jp',
      none: false,
    });

    // 俳優を選択（modelsQuery.dataがある場合）。推奨性別が分かれば性別一致を優先し、
    // 該当が無ければ従来どおり一様ランダム。
    if (modelsQuery.data && modelsQuery.data.length > 0) {
      const preferredGender = recommendations?.status === 'ready' ? recommendations.person?.gender : undefined;
      const genderMatchedModels = preferredGender
        ? modelsQuery.data.filter((m) => m.gender === preferredGender)
        : [];
      const modelPool = genderMatchedModels.length > 0 ? genderMatchedModels : modelsQuery.data;
      const randomModel = modelPool[Math.floor(Math.random() * modelPool.length)];
      setSelectedModelId(randomModel.id);
      setSettings((prev) => ({
        ...prev,
        model: randomModel.id,
        modelMeta: randomModel,
      }));
    }

    // 画像生成設定: Gemini 標準, 1K (512px は 2026-10-08 に廃止), トリミングなし, アスペクト比を復元
    setSettings((prev) => ({
      ...prev,
      generation_model: GENERATION_MODEL_SPEED,
      image_size: '1K',
      auto_trim_resolution: false,
      aspect_ratio: currentAspectRatio,
    }));
  }, [selectedAssets, orderedAttributeTree, templateGroups, settings.attributes, settings.aspect_ratio, setAttribute, randomize, randomizeWithRecommendations, recommendationsQuery.data, toast, safeModeQuery.data, modelsQuery.data, setSettings, recommendationSubject, logRecommendationEvent]);

  // 人物セクションの設定サマリー
  const personSummary = useMemo(() => {
    const attrs = settings.attributes || {};
    const person = (attrs.person as Record<string, unknown>) || {};

    if (person.none === true) {
      return '人物なし';
    }

    const items: string[] = [];

    if (settings.model) {
      const model = settings.modelMeta as { display_name?: string } | null;
      if (model?.display_name) {
        items.push(`モデル: ${model.display_name}`);
      }
    } else {
      if (person.gender && person.gender !== 'none') {
        const node = orderedAttributeTree.find((n) => n.key === 'person');
        const genderNode = node?.children?.find((c) => c.key === 'gender');
        const child = genderNode?.children?.find((c) => c.key === person.gender);
        if (child) items.push(child.label);
      }
      if (person.ethnicity && person.ethnicity !== 'none' && person.ethnicity !== 'jp') {
        const node = orderedAttributeTree.find((n) => n.key === 'person');
        const ethnicityNode = node?.children?.find((c) => c.key === 'ethnicity');
        const child = ethnicityNode?.children?.find((c) => c.key === person.ethnicity);
        if (child) items.push(child.label);
      }
      if (typeof person.age === 'number') {
        items.push(`${person.age}歳`);
      }
    }

    // 属性ラベルを再帰的に探す（2階層目以降も対応）
    type AttrNode = { key: string; label: string; children?: AttrNode[] };
    const findLabel = (children: AttrNode[] | undefined, targetKey: string): string | null => {
      if (!children) return null;
      for (const c of children) {
        if (c.key === targetKey) return c.label;
        if (c.children) {
          const found = findLabel(c.children, targetKey);
          if (found) return found;
        }
      }
      return null;
    };

    // 人物配下のセレクト系フィールド (体格・身体的特徴 / 表情 / ファッション / パートナー追加の group) を
    // ツリー順に走査し、選択肢のラベルを出す (入れ子 group 配下の option も拾う)
    const personNode = orderedAttributeTree.find((n) => n.key === 'person');
    const personChildren = personNode?.children ?? [];
    for (const attrNode of personChildren) {
      if (attrNode.type !== 'group' || attrNode.key === 'none' || attrNode.key === 'gender' || attrNode.key === 'ethnicity') continue;
      const val = person[attrNode.key];
      if (typeof val === 'string' && val !== 'none') {
        const label = findLabel(attrNode.children as AttrNode[] | undefined, val);
        if (label) items.push(label);
      }
    }
    // 旧キー (「体格・身体的特徴」統合前の person.build / person.appearance) で保存された値は、
    // 孫以降の group から選択肢を探す (backend も同じ読み替えで描画する)
    for (const legacyKey of ['build', 'appearance']) {
      if (personChildren.some((c) => c.key === legacyKey)) continue;
      const val = person[legacyKey];
      if (typeof val === 'string' && val !== 'none') {
        const label = findLabel(personChildren as AttrNode[], val);
        if (label) items.push(label);
      }
    }

    return items.length > 0 ? items.join(', ') : '設定なし';
  }, [settings.attributes, settings.model, settings.modelMeta, orderedAttributeTree]);

  // 選択中の属性が商品情報を必要とするかチェック（該当エフェクト名を返す）
  const requiresProductInfoEffectLabel = useMemo<string | null>(() => {
    const attrs = settings.attributes || {};
    for (const group of orderedAttributeTree) {
      const selectedValue = attrs[group.key];
      if (!selectedValue || selectedValue === 'none') continue;
      const option = group.children?.find((c) => c.key === selectedValue);
      if (option?.metadata?.requires_product_info) return option.label;
    }
    return null;
  }, [settings.attributes, orderedAttributeTree]);


  // 背景・構図セクションの統合サマリー。
  // tree 駆動: アコーディオン本体 (composition 配下の子ノードを描画) と同じノードを
  // 同じ順で走査し、選択済みの値のラベルを反映する。ハードコードのキー列挙はしない
  // ため、管理画面で追加・移植したグループ (構図 / カメラアングル / 全体の雰囲気・印象 等) も
  // 自動でサマリーに載る (旧 imageSummary / styleSummary のキー欠落バグを防ぐ)。
  const compositionSummary = useMemo(() => {
    const attrs = settings.attributes || {};

    // 選択肢ラベルを再帰探索 (option が入れ子 group 配下にあっても拾う)
    const findOptionLabel = (
      children: AttributeTreeNode[] | undefined,
      optionKey: string,
    ): string | null => {
      if (!children) return null;
      for (const child of children) {
        if (child.key === optionKey) return child.label;
        const found = findOptionLabel(child.children, optionKey);
        if (found) return found;
      }
      return null;
    };

    const items: string[] = [];

    const compositionNode = (attributeTree ?? []).find((n) => n.key === 'composition');
    const children = [...(compositionNode?.children ?? [])].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
    );
    for (const node of children) {
      const val = attrs[node.key];
      if (typeof val === 'string' && val && val !== 'none') {
        const label = findOptionLabel(node.children, val);
        if (label) items.push(label);
      }
    }

    if (settings.include_logo) {
      items.push('ロゴ追加');
    }
    // 詳細設定が閉じていても見失わないよう、レイアウト適用中はサマリーに出す
    if (bannerLayout) {
      items.push(`レイアウト: 要素${bannerLayout.elements.length}個`);
    }
    if (thinkingLevel === 'high') {
      items.push('熟考');
    } else if (thinkingLevel === 'minimal') {
      items.push('最小限の思考');
    }

    return items.length > 0 ? items.join(', ') : '設定なし';
  }, [settings.attributes, settings.include_logo, attributeTree, thinkingLevel, bannerLayout]);

  // テキストセクションの設定サマリー
  const textSummary = useMemo(() => {
    const mainCopy = (settings.attributes?.main_copy as string | undefined) ?? '';
    const cta = (settings.attributes?.cta as string | undefined) ?? '';
    const otherInstructions = (settings.attributes?.other_instructions as string | undefined) ?? '';
    const generateTextFromEc = settings.attributes?.generate_text_from_ec as boolean | string | undefined;
    const disclaimerText = (settings.attributes?.disclaimer_text as string | undefined) ?? '';
    const displayText = (settings.attributes?.display_text as string | undefined) ?? '';
    const priceText = (settings.attributes?.price_text as string | undefined) ?? '';
    const dateText = (settings.attributes?.date_text as string | undefined) ?? '';

    const items = [];
    if (mainCopy.trim()) items.push(`メインコピー: ${mainCopy}`);
    if (generateTextFromEc === true) items.push(`${brandConfig.labels.productShort}情報からテキスト生成`);
    if (generateTextFromEc === 'never') items.push('テキスト自動生成なし');
    if (cta.trim()) items.push(`CTA: ${cta}`);
    if (disclaimerText.trim()) items.push(`注意書き: ${disclaimerText}`);
    if (displayText.trim()) items.push(`テキスト: ${displayText}`);
    if (priceText.trim()) items.push(`価格: ${priceText}`);
    if (dateText.trim()) items.push(`日付: ${dateText}`);
    if (otherInstructions.trim()) items.push(`その他: ${otherInstructions}`);

    return items.length > 0 ? items.join(', ') : '設定なし';
  }, [settings.attributes]);

  // sticky状態を検出
  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px',
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sentinel = stickySentinelRef.current;
      if (!sentinel) return;
      const rect = sentinel.getBoundingClientRect();
      setIsSticky(rect.bottom <= 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // トップページから初期プロンプト付きで遷移した場合、pendingPromptに設定
  useEffect(() => {
    const initialPrompt = location.state?.initialPrompt as string | undefined;
    if (!initialPrompt) return;

    const path = location.pathname;
    if (path.startsWith('/studio/video')) {
      setPendingVideoGeneration(initialPrompt);
    } else {
      setPendingEdit(initialPrompt);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // sticky解除時（最上部に戻った時）は折りたたみ状態を解除
  useEffect(() => {
    if (!isSticky && isStickyCollapsed) {
      setIsStickyCollapsed(false);
    }
  }, [isSticky, isStickyCollapsed]);

  const collapseSticky = () => {
    setIsStickyCollapsed(true);
  };

  const expandSticky = () => {
    setIsStickyCollapsed(false);
  };

  // シナリオモード (属性フォーム) では選択素材エリアをスティッキー化しない
  // (シェブロンの折り畳みトグルも非表示にする)。
  // - 絵コンテから制作: 役目はストーリーボード画像生成までで終わる (従来からの除外)
  // - シーン単位で制作: 素材の役目はシナリオ生成・フレーム生成の入力までで、
  //   シーン編集への追従は不要 (2026-08-31 ユーザー指定。生成フレームの自動追加で
  //   バーが伸びやすくなったことも背景)
  // プロンプト入力モードと静止画タブは従来どおり sticky。
  const isStoryboardMode = activeTab === 'scenario' && videoGenerationMode === 'storyboard';
  const isScenarioAttributesMode = activeTab === 'scenario' && scenarioMode !== 'prompt';
  const stickyActive = isSticky && !isScenarioAttributesMode;
  // 参照画像のモデル上限グレーアウトを効かせるモード判定。参考画像モード (reference) のみ対象。
  // フレームは専用オーバーレイ、ストーリーボードは選択素材を商品タイル1枚へ統合するため
  // (個別 reference_images として送らない) 個別グレーアウトはしない。
  const isPromptFrameMode = activeTab === 'scenario' && scenarioMode === 'prompt' && promptVideoGenerationMode === 'frame';
  // モーショングラフィックス (3 タブ目) 中はレイヤー素材のチップに「N レイヤー」バッジ (クリックでレイヤーのモーダル・§5.2)。
  // タブが出ない条件では motion は reference へ寄せられる途中なので出さない
  const isPromptMotionMode = activeTab === 'scenario' && scenarioMode === 'prompt'
    && promptVideoGenerationMode === 'motion' && motionTabVisible;
  // 同じ値を addAsset の「レイヤー素材は 1 件」の判定にも使う (表示条件と一本化・パスは見ない)
  useEffect(() => {
    setPromptMotionModeActive(isPromptMotionMode);
  }, [isPromptMotionMode, setPromptMotionModeActive]);
  useEffect(() => () => setPromptMotionModeActive(false), [setPromptMotionModeActive]);
  const isReferenceGrayoutMode =
    (activeTab === 'scenario' && scenarioMode === 'attributes' && videoGenerationMode === 'reference') ||
    (activeTab === 'scenario' && scenarioMode === 'prompt' && promptVideoGenerationMode === 'reference');
  // 静止画タブでは音声と「動画そのまま」の動画素材は生成に使われない (動画プロンプト入力
  // モードと選択スコープを共有しているため残り得る)。GenerationPreviewPanel の
  // preparedAssets の除外と対で、チップをグレーアウトして知らせる。
  const stillImageUnusedAssetIds = useMemo(() => (
    activeTab === 'stillImage'
      ? selectedAssets.filter((asset) => !isStillImageUsableAsset(asset)).map((asset) => asset.id)
      : []
  ), [activeTab, selectedAssets]);
  // Seedance (BytePlus) は参照画像の短辺 300px 未満を 400 で拒否する (アップスケールは
  // しない方針)。選択中の小さすぎる画像素材をグレーアウトして知らせる
  // (実際の送信除外は buildVideoPayload / handleGenerateSceneVideo 側。バックエンドにも
  // 同じ閾値のフェイルセーフあり)。対象モード:
  // - 参考画像モード (個別 reference_images として送るため)
  // - ストーリーボードの個別添付モデル (選択素材を storyboard_attached_assets で個別に
  //   送るため。従来モデルはタイル1枚へ合成され小画像も使われるので対象外)
  const isSeedanceVideoModel = (videoSettings.videoModel || '').startsWith('seedance');
  const { data: videoModelsForRefs = [] } = useGenerationModels('video');
  const isStoryboardIndividualRefs =
    isStoryboardMode && isStoryboardIndividualRefsModel(videoSettings.videoModel, videoModelsForRefs);
  const seedanceSmallImageAssetIds = useMemo(() => (
    (isReferenceGrayoutMode || isStoryboardIndividualRefs) && isSeedanceVideoModel
      ? selectedAssets
          // 300px 未満でも backend が補正して送る (reference-image-upscale-plan.md)。補正できない極小だけ除外
          .filter((asset) => isBelowMinReferenceSize(asset, SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR))
          .map((asset) => asset.id)
      : []
  ), [isReferenceGrayoutMode, isStoryboardIndividualRefs, isSeedanceVideoModel, selectedAssets]);
  // 選択中の動画モデルの最短クリップ尺 (Seedance系=1.8秒/MiniMax H3=2秒。それ以外の
  // 参照音声非対応モデルは null=制約なし) 未満の参照音声チップをグレーアウトする。
  // 実送信からの除外は useVideoHandlers.ts の buildVideoPayload 側 (同じ判定関数を使用)。
  const shortReferenceAudioAssetIds = useMemo(() => (
    selectedAssets
      .filter((asset) => isReferenceAudioBelowMinClipSeconds(asset, videoSettings.videoModel))
      .map((asset) => asset.id)
  ), [selectedAssets, videoSettings.videoModel]);
  const referenceAudioMinClipSeconds = getReferenceAudioMinClipSeconds(videoSettings.videoModel);
  const combinedDisabledAssetIds = useMemo(() => {
    const merged = Array.from(new Set([...stillImageUnusedAssetIds, ...seedanceSmallImageAssetIds, ...shortReferenceAudioAssetIds]));
    return merged.length > 0 ? merged : undefined;
  }, [stillImageUnusedAssetIds, seedanceSmallImageAssetIds, shortReferenceAudioAssetIds]);
  // SelectedAssetsPanel 上部のグレーアウト理由バナー文言。複数の理由が同時に発生し得る
  // ため (例: 参照上限 + 小画像 + 短尺音声)、該当する文言を配列で組み立てて連結する。
  const disabledWarningText = useMemo(() => {
    const parts: string[] = [];
    if (isReferenceGrayoutMode) {
      parts.push(`このモデルでは俳優と参考画像を合わせて先頭から${referenceImageLimit}枚のみ利用されます。`);
    } else if (isPromptFrameMode) {
      parts.push('「静止画フレームから動画化」では最初と最後のフレーム素材のみ利用されます。');
    }
    if (seedanceSmallImageAssetIds.length > 0) {
      parts.push(`短辺${SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR}px未満の画像はこのモデルでは使用されません（300px未満は拡大して送られます）。`);
    }
    if (shortReferenceAudioAssetIds.length > 0 && referenceAudioMinClipSeconds !== null) {
      parts.push(`${referenceAudioMinClipSeconds}秒未満の参照音声はこのモデルでは使用されません。`);
    }
    if (parts.length === 0 && stillImageUnusedAssetIds.length > 0) {
      parts.push('動画・音声は静止画の生成には使われません。動画は「最初/最後のフレーム」を指定すると参考画像として使えます。');
    }
    return parts.length > 0 ? parts.join('') : undefined;
  }, [
    isReferenceGrayoutMode, isPromptFrameMode, referenceImageLimit,
    seedanceSmallImageAssetIds.length, shortReferenceAudioAssetIds.length, referenceAudioMinClipSeconds,
    stillImageUnusedAssetIds.length,
  ]);
  const hasSelectedActors =
    !!selectedCasting ||
    additionalCastings.length > 0 ||
    !!selectedSeedancePresetActor ||
    additionalSeedancePresetActors.length > 0;
  const removeCastingAt = (index: number) => {
    const next = [
      ...(selectedCasting ? [selectedCasting] : []),
      ...additionalCastings,
    ].filter((_, i) => i !== index);
    const [primary, ...additional] = next;
    setSelectedCasting(primary ?? null);
    setAdditionalCastings(additional);
  };
  const removeSeedancePresetAt = (index: number) => {
    const next = [
      ...(selectedSeedancePresetActor ? [selectedSeedancePresetActor] : []),
      ...additionalSeedancePresetActors,
    ].filter((_, i) => i !== index);
    const [primary, ...additional] = next;
    setSelectedSeedancePresetActor(primary ?? null);
    setAdditionalSeedancePresetActors(additional);
  };

  return (
    <div className={styles.workspacePage}>
      <div className={styles.container}>
        <div ref={stickySentinelRef} className={styles.stickySentinel} aria-hidden="true" />
        <div
          ref={stickyRef}
          className={`${styles.selectedAssetsSticky} ${stickyActive ? styles.selectedAssetsStickyActive : ''} ${activeTab === 'scenario' && scenarioMode !== 'prompt' && selectedAssets.length === 0 && !hasSelectedActors ? styles.selectedAssetsStickyVideoHidden : ''}`}
          // シナリオモード (属性フォーム) では sticky 化しない (上の isScenarioAttributesMode
          // コメント参照)。.selectedAssetsSticky の position: sticky を inline で打ち消す。
          style={isScenarioAttributesMode ? { position: 'static' } : undefined}
        >
          <section
            className={`${styles.section} ${styles.stickySection} ${activeTab === 'scenario' && scenarioMode !== 'prompt' && selectedAssets.length === 0 && !hasSelectedActors ? styles.stickySectionVideoHidden : isStickyCollapsed ? styles.stickySectionCollapsed : styles.stickySectionExpanded}`}
          >
            {(selectedAssets.length > 0 || hasSelectedActors) && (
              <div className={`${styles.clearButtonContainer} ${isAssetsScrolledToEnd ? styles.clearButtonNoBg : ''}`}>
                <button type="button" className={styles.textLink} onClick={() => { clearAssets(); setSelectedCasting(null); setAdditionalCastings([]); setSelectedSeedancePresetActor(null); setAdditionalSeedancePresetActors([]); }}>
                  すべてクリア
                </button>
              </div>
            )}
            <SelectedAssetsPanel
              assets={selectedAssets}
              casting={selectedCasting}
              additionalCastings={additionalCastings}
              onCastingRemove={removeCastingAt}
              seedancePreset={selectedSeedancePresetActor}
              additionalSeedancePresets={additionalSeedancePresetActors}
              onSeedancePresetRemove={removeSeedancePresetAt}
              onClearAll={(selectedAssets.length > 0 || hasSelectedActors) ? () => { clearAssets(); setSelectedCasting(null); setAdditionalCastings([]); setSelectedSeedancePresetActor(null); setAdditionalSeedancePresetActors([]); } : undefined}
              onScrollEndChange={setIsAssetsScrolledToEnd}
              disabledFromIndex={
                isReferenceGrayoutMode ? referenceImageLimit
                : isPromptFrameMode ? 2
                : undefined
              }
              disabledAssetIds={combinedDisabledAssetIds}
              disabledWarningText={disabledWarningText}
              onReorder={
                isReferenceGrayoutMode || isPromptFrameMode
                  ? reorderAssets
                  : undefined
              }
              castingPosition={isReferenceGrayoutMode ? castingPosition : undefined}
              onCastingPositionChange={isReferenceGrayoutMode ? setCastingPosition : undefined}
              onOpenLayerModal={isPromptMotionMode ? openLayerModal : undefined}
              mentionActorGate={{
                isSeedanceModel: isSeedanceVideoModel,
                isStylized: visualStyleIsStylized,
                includeCastingSheet: videoSettings.includeCastingSheet !== false,
              }}
              frameModeOverlay={isPromptFrameMode}
              framePlaceholderCount={isPromptFrameMode ? 2 : undefined}
              framePlaceholderAspectRatio={
                isPromptFrameMode
                  ? (videoSettings.aspectRatio === '9:16' ? '9:16' : '16:9')
                  : undefined
              }
            />
          </section>
          {stickyActive && (activeTab !== 'scenario' || scenarioMode === 'prompt' || selectedAssets.length > 0 || hasSelectedActors) && (
            <button
              type="button"
              className={`${styles.stickyToggleButton} ${isStickyCollapsed ? styles.stickyToggleButtonCollapsed : ''}`}
              onClick={isStickyCollapsed ? expandSticky : collapseSticky}
              aria-label={isStickyCollapsed ? "選択素材エリアを開く" : "選択素材エリアを閉じる"}
              aria-expanded={!isStickyCollapsed}
              title={isStickyCollapsed ? "開く" : "閉じる"}
            >
              <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 5L6 1L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>

        <div className={styles.tabContainer}>
          {/* テンプレート特化 UI は静止画のみ (動画タブ・タブ行ごと非表示) */}
          {!isTemplateOnlyUi && (
          <div className={styles.tabButtons}>
            <button
              type="button"
              className={`${styles.tabButton} ${brandConfig.id === 'hypeapp' ? styles.tabButtonSub : ''} ${activeTab === 'stillImage' ? styles.activeTab : ''}`}
              onClick={() => handleTabChange('stillImage')}
            >
              <img src="/picture_stack_landscape.svg" alt="静止画" className={styles.tabIcon} />
              <span>静止画生成</span>
            </button>
            <button
              type="button"
              className={`${styles.tabButton} ${styles.tabButtonVideo} ${activeTab === 'scenario' ? styles.activeTab : ''}`}
              onClick={() => handleTabChange('scenario')}
            >
              <img src="/video_player_movie.svg" alt="動画生成" className={styles.tabIcon} />
              <span>動画生成</span>
            </button>
          </div>
          )}

          <div className={styles.tabContent}>
            {activeTab === 'stillImage' ? (
              <>
          {/* テンプレート特化 UI はモード切替 (プロンプト入力) とリセット/ガチャ等の一括操作を出さない */}
          {!isTemplateOnlyUi && (
          <section className="gen-mode-section" style={{ position: 'relative' }}>
            <div className="gen-mode-switch">
              {brandConfig.id === 'hypeapp' ? (
                <>
                  <button
                    type="button"
                    className={`gen-mode-button ${stillImageMode === 'prompt' ? 'gen-mode-button-active' : ''}`}
                    onClick={() => handleModeChange('prompt')}
                  >
                    プロンプト入力<span className="gen-mode-button-suffix">モード</span>
                  </button>
                  {isPurposeModeAvailable && (
                    <button
                      type="button"
                      className={`gen-mode-button ${stillImageMode === 'purpose' ? 'gen-mode-button-active' : ''}`}
                      onClick={() => handleModeChange('purpose')}
                    >
                      目的から選ぶ
                    </button>
                  )}
                  <button
                    ref={attributeModeButtonRef}
                    type="button"
                    className={`gen-mode-button ${stillImageMode === 'attributes' ? 'gen-mode-button-active' : ''}`}
                    onClick={() => handleModeChange('attributes')}
                  >
                    {brandConfig.labels.attributeMode}
                  </button>
                </>
              ) : (
                <>
                  <button
                    ref={attributeModeButtonRef}
                    type="button"
                    className={`gen-mode-button ${stillImageMode === 'attributes' ? 'gen-mode-button-active' : ''}`}
                    onClick={() => handleModeChange('attributes')}
                  >
                    {brandConfig.labels.attributeMode}
                  </button>
                  {isPurposeModeAvailable && (
                    <button
                      type="button"
                      className={`gen-mode-button ${stillImageMode === 'purpose' ? 'gen-mode-button-active' : ''}`}
                      onClick={() => handleModeChange('purpose')}
                    >
                      目的から選ぶ
                    </button>
                  )}
                  <button
                    type="button"
                    className={`gen-mode-button ${stillImageMode === 'prompt' ? 'gen-mode-button-active' : ''}`}
                    onClick={() => handleModeChange('prompt')}
                  >
                    プロンプト入力<span className="gen-mode-button-suffix">モード</span>
                  </button>
                </>
              )}
            </div>
            {coachMarkMessage && (
              <div
                className="coach-mark coach-mark-arrow-top coach-mark-safe-mode"
                style={{ '--coach-mark-max-width': '400px', '--coach-mark-z-index': 'calc(var(--z-panel) - 1)' } as React.CSSProperties}
                onClick={() => setCoachMarkMessage(null)}
              >
                {coachMarkMessage}
              </div>
            )}
            {stillImageMode === 'attributes' && (
              <div className="gen-accordion-controls">
                <div className={styles.iconButtonGroup}>
                  <Tooltip content="すべてリセット">
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={handleResetAll}
                    >
                      <X size={16} />
                    </button>
                  </Tooltip>
                  <Tooltip content={gachaTooltip}>
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={handleGacha}
                      disabled={gachaMode === 'pending'}
                    >
                      <img src="/shuffle_angular_light.svg" alt={gachaLabel} style={{ width: '16px', height: '16px' }} />
                    </button>
                  </Tooltip>
                  {/* すべて開く ⇔ すべて閉じる の1ボタントグル (全セクションが開いていれば「閉じる」) */}
                  <Tooltip content={areAllAccordionsOpen ? 'すべて閉じる' : 'すべて開く'}>
                    <button
                      type="button"
                      className={styles.iconButton}
                      onClick={areAllAccordionsOpen ? closeAllAccordions : openAllAccordions}
                    >
                      {areAllAccordionsOpen ? (
                        <img src="/move_collapse_vertical.svg" alt="すべて閉じる" style={{ width: '16px', height: '16px' }} />
                      ) : (
                        <img src="/move_expand_vertical.svg" alt="すべて開く" style={{ width: '16px', height: '16px' }} />
                      )}
                    </button>
                  </Tooltip>
                </div>
              </div>
            )}
          </section>
          )}

          {stillImageMode === 'attributes' ? (
            <>
            {attributeQuery.isLoading ? (
              <section className={styles.section}>
                <div className={styles.loading}>属性データを読み込み中…</div>
              </section>
            ) : attributeQuery.isError ? (
              <section className={styles.section}>
                <div className={styles.error}>属性の取得に失敗しました</div>
              </section>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* purpose_only テンプレ (バナー等) 適用中は通常アコーディオンに現れないため、
                    ここに来て初めて他属性がロックされている理由がわかるよう通知バーを出す。
                    order: -1 でアコーディオン群 (order は最小でも 10000〜) の先頭に固定する。 */}
                {activeTemplate && !visibleTemplateGroups.some((g) => g.key === activeTemplate.group.key) && (
                  <div className={styles.purposeTemplateNotice} style={{ order: -1 }}>
                    <span>
                      バナーテンプレート「{activeTemplate.label}」を適用中です。他の属性はロックされています。
                    </span>
                    <div className={styles.purposeTemplateNoticeActions}>
                      <button
                        type="button"
                        className={styles.purposeTemplateNoticeButton}
                        onClick={() => handleModeChange('purpose')}
                      >
                        テンプレートを変更
                      </button>
                      <button
                        type="button"
                        className={styles.purposeTemplateNoticeButton}
                        onClick={() => setAttribute(activeTemplate.group.key, 'none')}
                      >
                        解除
                      </button>
                    </div>
                  </div>
                )}
                {/* テンプレートセクション (section='template' のトップレベル group)。
                    purpose_only (バナー等・目的軸モード専用) は通常アコーディオンに出さない。 */}
                {visibleTemplateGroups.map((node) => {
                  const rawValue = settings.attributes?.[node.key];
                  return (
                    <TemplateSection
                      key={node.id}
                      node={node}
                      order={accordionOrderFor(TEMPLATE_SECTION_KEY, node.sort_order ?? 0)}
                      isOpen={!!extraAccordionOpen[node.key]}
                      onToggle={() => toggleExtraAccordion(node.key)}
                      selectedKey={typeof rawValue === 'string' ? rawValue : 'none'}
                      activeTemplate={activeTemplate}
                      onSelectOption={selectTemplateOption}
                      templateReferenceUrl={templateReferenceUrl}
                      onChangeTemplateReferenceUrl={setTemplateReferenceUrl}
                      brandUrlChoices={brandUrlChoices}
                    />
                  );
                })}

                {/* 背景・構図セクション。テンプレート適用中は閉じて操作不可にし、注記をアコーディオンに重ねる */}
                {hasComposition && !isTemplateOnlyUi && (
                <CompositionSection
                  order={accordionOrderFor('composition', 0)}
                  attributeTree={attributeTree}
                  settings={settings}
                  setSettings={setSettings}
                  onAttributeChange={handleAttributeChange}
                  recommendedKeysByGroup={activeRecommendedKeysByGroup}
                  compositionSummary={compositionSummary}
                  requiresProductInfoEffectLabel={requiresProductInfoEffectLabel}
                  selectedAssets={selectedAssets}
                  hasAnyProductLinks={hasAnyProductLinks}
                  brandLogosAvailable={brandLogosAvailable}
                  extractedAsins={extractedAsins}
                  brandsWithoutLogos={brandsWithoutLogos}
                  selectedBackgroundRequiresLogo={selectedBackgroundRequiresLogo}
                  closeMaterialsPanel={closeMaterialsPanel}
                  isTemplateActive={isTemplateActive}
                  templateLockClass={templateLockClass}
                  renderOpen={compositionRenderOpen}
                  onToggle={() => setIsImageSectionOpen(!isImageSectionOpen)}
                  onClose={() => setIsImageSectionOpen(false)}
                  bannerLayoutEnabled={isBannerLayoutEnabled}
                />
                )}
                {isBannerLayoutEnabled && !isTemplateOnlyUi && (
                  <BannerLayoutEditorHost
                    settings={settings}
                    setSettings={setSettings}
                    selectedAssets={selectedAssets}
                    logoBrandNames={logoBrandNames}
                  />
                )}

                {/* 人物セクション。テンプレート適用中は閉じて操作不可にし、注記をアコーディオンに重ねる */}
                {hasPerson && !isTemplateOnlyUi && (
                <PersonSection
                  order={accordionOrderFor('person', 0)}
                  orderedAttributeTree={orderedAttributeTree}
                  settings={settings}
                  setSettings={setSettings}
                  onAttributeChange={handleAttributeChange}
                  recommendedKeysByGroup={activeRecommendedKeysByGroup}
                  personSummary={personSummary}
                  modelsQuery={modelsQuery}
                  selectedModelId={selectedModelId}
                  setSelectedModelId={setSelectedModelId}
                  onModelToggle={handleModelToggle}
                  isTemplateActive={isTemplateActive}
                  templateLockClass={templateLockClass}
                  renderOpen={personRenderOpen}
                  onToggle={() => setIsPersonSectionOpen(!isPersonSectionOpen)}
                  onClose={() => setIsPersonSectionOpen(false)}
                />
                )}

                {/* スタイルセクションは廃止し、エフェクト/全体の雰囲気・印象/詳細設定を
                    背景・構図アコーディオンに統合した */}

                {/* OEM が追加したトップレベル group 用の汎用アコーディオン */}
                {!isTemplateOnlyUi && extraTopLevelGroups.map(({ node, order }) => (
                  <ExtraGroupSection
                    key={node.id}
                    node={node}
                    order={order}
                    isOpen={!!extraAccordionOpen[node.key]}
                    onToggle={() => toggleExtraAccordion(node.key)}
                    settings={settings}
                    onAttributeChange={handleAttributeChange}
                    recommendedKeysByGroup={activeRecommendedKeysByGroup}
                    templateLockClass={templateLockClass}
                  />
                ))}

                {/* テキストセクション */}
                {!isTemplateOnlyUi && (
                <TextSection
                  settings={settings}
                  setSettings={setSettings}
                  setAttribute={setAttribute}
                  textSummary={textSummary}
                  hasAnyProductLinks={hasAnyProductLinks}
                  brandDescriptionAvailable={brandDescriptionAvailable}
                  brandTaglines={brandTaglines}
                  showHistory={showAttrPromptHistory}
                  onToggleHistory={() => setShowAttrPromptHistory(!showAttrPromptHistory)}
                  onCloseHistory={() => setShowAttrPromptHistory(false)}
                  onSelectHistory={handleSelectAttrPromptHistory}
                  isOpen={isTextSectionOpen}
                  onToggle={() => setIsTextSectionOpen(!isTextSectionOpen)}
                  onClose={() => setIsTextSectionOpen(false)}
                  layoutApplied={!!bannerLayout}
                />
                )}
              </div>
            )}
            </>
          ) : stillImageMode === 'purpose' && isPurposeModeAvailable ? (
            <PurposeModePanel
              subject={recommendationSubject}
              activeTemplate={activeTemplate}
              activeTemplateIsPurposeOnly={activeTemplateIsPurposeOnly}
              copyValues={{
                mainCopy: (settings.attributes?.main_copy as string | undefined) ?? '',
                cta: (settings.attributes?.cta as string | undefined) ?? '',
              }}
              onChangeCopy={(field, value) => setAttribute(field, value)}
              onSelectBannerTemplate={(groupKey, optionKey) => {
                selectTemplateOption(groupKey, optionKey);
                // バナーは常に 1:1 (purpose_service._ASPECT_RATIO_BY_PURPOSE と同期)。
                // テンプレート選択という明示操作の一部として常に適用する。
                setSettings((prev) => ({ ...prev, aspect_ratio: '1:1' }));
              }}
              onClearBannerTemplate={(groupKey) => setAttribute(groupKey, 'none')}
              onApplyPreset={({ aspectRatio, presetSelections, personIncluded }) => {
                // テンプレ適用中はバックエンドがプロンプトをテンプレ全文で差し替えるため、
                // プリセットの属性が残っていても一切効かない。プリセット適用時は先にテンプレを解除する。
                if (activeTemplate) {
                  setAttribute(activeTemplate.group.key, 'none');
                }
                for (const [key, value] of Object.entries(presetSelections)) {
                  setAttribute(key, value);
                }
                // person.none は既存の状態表現 (プロンプト生成側 none_person/person_included と同期。
                // backend/services/image_generation/prompt_builder.py 参照)。AIおまかせ (handleGacha) と
                // 同様に person オブジェクトへマージして適用する。
                const currentPerson = (settings.attributes?.person as Record<string, unknown>) || {};
                setAttribute('person', { ...currentPerson, none: !personIncluded });
                setSettings((prev) => ({ ...prev, aspect_ratio: aspectRatio }));
                // 計画書§6の「調整して生成」導線: 属性画面へ合流する
                handleModeChange('attributes');
              }}
              attributeTree={attributeTree}
            />
          ) : null}

          {stillImageMode === 'attributes' && (
            <MediaRequirementsAccordion
              isOpen={isMediaRequirementsOpen}
              onToggle={() => setIsMediaRequirementsOpen(!isMediaRequirementsOpen)}
              onClose={() => setIsMediaRequirementsOpen(false)}
              mode="attributes"
              thinkingLevel={thinkingLevel}
              onThinkingLevelChange={setThinkingLevel}
            />
          )}
          {/* 目的軸モードでバナーテンプレを選んだ場合も、ここから通常のテンプレート生成経路
              (mode="attributes" 相当) にそのまま乗せる。商品画像/シーン/SNS はプリセット適用後に
              handleModeChange('attributes') で属性モードへ遷移するため、この生成ボタンは
              実質的に「属性モード」の状態を見て動く。 */}
          <GenerationPreviewPanel
            mode={stillImageMode === 'prompt' ? 'prompt' : 'attributes'}
            onModeChange={handleModeChange}
            pendingPrompt={pendingPrompt}
            onClearPendingEdit={clearPendingEdit}
            isProductInfoMissing={!!requiresProductInfoEffectLabel && selectedAssets.length > 0 && !hasAnyProductLinks}
            thinkingLevel={thinkingLevel}
            promptFooter={stillImageMode === 'prompt' && (hasAnyProductLinks || brandsAvailable || brandLogosAvailable) ? (
              // プロンプト入力モードの「AIに送る情報」。属性モードが自動で付ける商品情報 / ブランド情報 / ロゴを利用者が選ぶ
              // (既定は全て OFF・localStorage に保存)。選択中の素材から解決できるものだけトグルを出し、何も無ければ行ごと出さない。
              // 「ロゴを追加する」は属性モード (CompositionSection) と同じ settings.include_logo
              <div className={styles.promptContextToggles}>
                <span className={styles.promptContextTogglesLabel}>AIに送る情報</span>
                {hasAnyProductLinks && (
                  <ToggleSwitch
                    checked={settings.include_product_info ?? false}
                    onChange={(checked) => setSettings((prev) => ({ ...prev, include_product_info: checked }))}
                    label={`${brandConfig.labels.productShort}情報`}
                  />
                )}
                {brandsAvailable && (
                  <ToggleSwitch
                    checked={settings.include_brand_info ?? false}
                    onChange={(checked) => setSettings((prev) => ({ ...prev, include_brand_info: checked }))}
                    label="ブランド情報"
                  />
                )}
                {brandLogosAvailable && (
                  <ToggleSwitch
                    checked={settings.include_logo ?? false}
                    onChange={(checked) => setSettings((prev) => ({ ...prev, include_logo: checked }))}
                    label="ロゴ"
                  />
                )}
              </div>
            ) : undefined}
            beforeGenerateButton={stillImageMode === 'prompt' ? (
              <MediaRequirementsAccordion
                isOpen={isMediaRequirementsOpen}
                onToggle={() => setIsMediaRequirementsOpen(!isMediaRequirementsOpen)}
                onClose={() => setIsMediaRequirementsOpen(false)}
                mode="prompt"
                thinkingLevel={thinkingLevel}
                onThinkingLevelChange={setThinkingLevel}
              />
            ) : undefined}
          />
              </>
            ) : (
              <section className={styles.section}>
                <ScenarioGenerator initialMode={scenarioMode} onModeChange={handleScenarioModeChange} />
              </section>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showVideoEditConfirm}
        title="動画プロンプト入力モードに切り替えますか？"
        message="静止画生成では動画素材を使えません。動画プロンプト入力モードに切り替えると、この動画を参照動画（video-to-video）として編集できます。"
        variant="info"
        confirmLabel="切り替える"
        cancelLabel="キャンセル"
        onConfirm={acceptVideoEditOffer}
        onCancel={clearVideoEditOffer}
      />

      <ConfirmDialog
        isOpen={!!v2vSwitchConfirm}
        title="参照動画は使用されません"
        message={`このモデルは参照動画（video-to-video）に対応していません。選択中の参照動画 ${v2vSwitchConfirm?.videoAssetIds.length ?? 0} 本を外してこのモデルに切り替えますか？`}
        variant="warning"
        confirmLabel="外して切り替え"
        cancelLabel="元のモデルに戻す"
        onConfirm={confirmV2vSwitch}
        onCancel={cancelV2vSwitch}
      />

      <ConfirmDialog
        isOpen={!!aspectRatioConfirm}
        title="アスペクト比の変更"
        message={aspectRatioConfirm?.reason || ''}
        variant="info"
        hideButtons
        onConfirm={() => {
          if (aspectRatioConfirm) {
            setSettings((prev) => ({ ...prev, aspect_ratio: aspectRatioConfirm.targetRatio }));
          }
          setAspectRatioConfirm(null);
        }}
        onCancel={() => setAspectRatioConfirm(null)}
      >
        {aspectRatioConfirm && (
          <AspectRatioPreview
            currentRatio={aspectRatioConfirm.currentRatio}
            targetRatio={aspectRatioConfirm.targetRatio}
            onCancel={() => setAspectRatioConfirm(null)}
            onConfirm={() => {
              setSettings((prev) => ({ ...prev, aspect_ratio: aspectRatioConfirm.targetRatio }));
              setAspectRatioConfirm(null);
            }}
          />
        )}
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={!!mediaSpecSuggestion}
        title="媒体規格の適用"
        message={
          mediaSpecSuggestion
            ? `「${mediaSpecSuggestion.optionLabel}」には媒体規格「${[
                mediaSpecSuggestion.spec.platform_display_name,
                mediaSpecSuggestion.spec.display_name,
              ]
                .filter(Boolean)
                .join(' / ')}」がオススメです。媒体要件に適用しますか？`
            : ''
        }
        variant="info"
        confirmLabel="適用する"
        cancelLabel="そのまま"
        onConfirm={acceptMediaSpecSuggestion}
        onCancel={dismissMediaSpecSuggestion}
      />
    </div>
  );
}
